import Stack from "@mui/material/Stack"
import { useTheme } from "@mui/material/styles"
import { deepMergeObject, formatNumber, getCalendarFormat, TGenericListReturn, truncateText } from "@cakemail-org/ui-components-v2"
import { Chart as ChartJs, ChartOptions, registerables } from "chart.js"
import { useEffect } from "react"
import { Chart } from 'react-chartjs-2'
import { useTranslation } from "react-i18next"
import "./styles.scss"
import { TCharts } from "./types"
import "chartjs-adapter-moment"
import { affixToPercent } from "../../utils/data"


ChartJs.register(...registerables)


export function getBaseChartOptions(theme: any): ChartOptions {
    return {
        borderColor: theme.palette.warning.main,
        font: {
            family: theme.typography.fontFamily,
            size: 14
        },
        color: theme.palette.body1.main,
        normalized: true,
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        elements: {},
        plugins: {
            legend: {
                display: false,
                position: 'top' as const,
                title: {
                    font: {
                        family: theme.typography.body1L.fontFamily,
                        size: 16,
                        weight: theme.typography.body1L.fontWeight
                    },
                    color: theme.typography.body1L.color
                }
            },
            title: {
                display: false,
                font: {
                    family: theme.typography.h3.fontFamily,
                    size: 22,
                    weight: theme.typography.h3.fontWeight
                },
                color: theme.typography.h3.color
            },
            subtitle: {
                display: false,
                font: {
                    family: theme.typography.h3S.fontFamily,
                    size: 18,
                    weight: theme.typography.h3S.fontWeight
                },
                color: theme.typography.h3S.color
            },
            tooltip: {
                enabled: true,
                intersect: false,
                mode: 'index',

                backgroundColor: theme.palette.text.primary,
                padding: 12,
                cornerRadius: 4,

                titleFont: {
                    family: theme.typography.body1.fontFamily,
                    size: 14,
                    weight: theme.typography.body1.fontWeight,
                },
                titleColor: theme.palette.common.white,
                titleMarginBottom: 8,

                bodyFont: {
                    family: theme.typography.body1.fontFamily,
                    size: 14,
                    weight: theme.typography.body1.fontWeight,
                },
                bodyColor: theme.palette.common.white,

                bodySpacing: 8,

                boxWidth: 12,
                boxHeight: 12,
                boxPadding: 8,

                callbacks: {
                    //Title of tootip
                    beforeTitle: () => { },
                    title: (contextItems) => {
                        return contextItems[0].parsed.x ? getCalendarFormat({ unixDate: contextItems[0].parsed.x / 1000 }) : getCalendarFormat({})
                    },
                    afterTitle: () => { },

                    //Row of item
                    beforeLabel: () => { },
                    label: (context) => {
                        if (context?.parsed?._stacks?.x) {
                            const currentval = Object.values(context.parsed._stacks.x).filter(val => { return val === context.raw })[0]
                            return "" + formatNumber(currentval)
                        }
                        //@ts-ignore
                        return `${context.dataset?.metadata?.provider || ""} : ${affixToPercent(Number(context.formattedValue))}`
                    },
                    afterLabel: () => { },


                    //Before after all items
                    beforeBody: () => { },
                    afterBody: () => { },

                    //End element of tooltip
                    beforeFooter: () => { },
                    footer: () => { },
                    afterFooter: () => { },

                }


            }
        },
        datasets: {
            bar: {
                backgroundColor: theme.palette.wildSand.main,
                //indexAxis: "x",
                barPercentage: 0.8,
                categoryPercentage: 1,
            },
            bubble: {},
            doughnut: {},
            line: {
                backgroundColor: theme.palette.body1.main,
                borderColor: theme.palette.body1.main,
                borderWidth: 2,
                tension: 0.2,
                pointHoverRadius: 5,
                pointStyle: "circle",
                pointRadius: function (context) {
                    return context.raw?.y === 0 ? 0 : 3 // Hide points at 0 
                },
                pointBorderColor: theme.palette.boulder.main,
            },
            pie: {},
            polarArea: {},
            radar: {},
            scatter: {}
        },
        scales: {
            x: {
                ticks: {
                    font: {
                        family: theme.typography.body1L.fontFamily,
                        size: 16,
                        weight: "normal"
                    },
                    color: theme.typography.body1.color
                },
                border: {
                    color: theme.palette.text.primary,
                    width: 2,
                    display: true
                },
                beginAtZero: true,
                grid: {
                    display: false
                }
            },
            y: {
                ticks: {
                    padding: 24,
                    maxTicksLimit: 8,
                    font: {
                        family: theme.typography.body2L.fontFamily,
                        size: theme.typography.body2L.size,
                        weight: "normal"
                    },
                    color: theme.typography.body1.color,
                },
                beginAtZero: true,
                border: {
                    display: false
                },
                grid: {
                    color: function (context) {
                        if (context?.tick?.value === 0) {
                            return 'rgba(0, 0, 0, 0)'
                        }
                        return theme.palette.divider
                    },
                    lineWidth: function (context) {
                        if (context?.tick?.value === 0) {
                            return 0
                        }
                        return 1
                    }
                }
            }
        }
    }
}

export function getLinearChartOptions(theme: any): ChartOptions {
    return {
        scales: {
            x: {
                offset: false
            },
            y: {
                ticks: {
                    callback: function (value) {
                        return value + '%' // Add percentage to the right Y-axis ticks
                    }
                },
            },
            y1: {
                type: 'linear',
                position: 'right',
                ticks: {
                    padding: 24,
                    maxTicksLimit: 8,
                    font: {
                        family: theme.typography.body2L.fontFamily,
                        size: theme.typography.body2L.size,
                        weight: "normal"
                    },
                    color: theme.typography.body1.color,
                    callback: function (value, a, b) {
                        return value + '%' // Add percentage to the right Y-axis ticks
                    }
                },
                beginAtZero: true,
                border: {
                    display: false
                },
                grid: {
                    drawOnChartArea: false,
                    lineWidth: function (context) {
                        return 0
                    }
                }
            }
        }
    }
}

export function Charts({
    type = "bar",
    data = {
        datasets: []
    },
    maxHeight,
    height,
    options
}: TCharts) {
    const { t, i18n } = useTranslation()
    const theme = useTheme()

    let cOptions: ChartOptions = getBaseChartOptions(theme)

    if (type === "line") {
        cOptions = deepMergeObject(getLinearChartOptions(theme), cOptions)
        cOptions = deepMergeObject(cOptions, options)
    } else if (options) {
        cOptions = deepMergeObject(options, cOptions)
    }


    return <Stack className="graphContainer" sx={{ width: "100%", maxHeight: maxHeight, height: height }}>
        <Chart type={type} data={data} options={cOptions} />
    </Stack>
}